import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Shield, MessageCircle, BookOpen, FileSearch, Briefcase, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    question: "\"Can I really do this myself, or is disputing credit errors legit?\"",
    answer: "Great question — and one we see constantly on Reddit. Here's the truth: the FTC explicitly states that 'anything a credit dispute company can do legally, you can do yourself at little or no cost.' Credit dispute companies use the exact same dispute process available to you under the Fair Credit Reporting Act (FCRA). The 'scam' part is when they charge $100+/month for 6-12 months to send letters you could send yourself. Our kit teaches you exactly how to do it.",
    source: "FTC Consumer Advice",
  },
  {
    question: "\"I disputed items but they just verified everything. Now what?\"",
    answer: "This is the #1 complaint we see online. Here's why it happens: generic 'not mine' disputes get auto-processed and rubber-stamped as 'verified.' The bureaus process millions of disputes — yours needs to stand out. Our kit teaches you how to write specific, evidence-backed disputes that force actual investigation. We also cover 'method of verification' letters that make bureaus prove how they verified the item.",
    source: "Common Reddit frustration",
  },
  {
    question: "\"How long does this actually take? I've been trying for months.\"",
    answer: "Under the FCRA, credit bureaus have 30 days to investigate your dispute (45 in some cases). Most of our users see their first results within 30-45 days. The full process typically takes 2-4 months depending on how many items you're disputing. The key is strategic sequencing — dispute the right items in the right order. Random 'dispute everything' approaches waste months.",
    source: "FCRA Guidelines",
  },
  {
    question: "\"What's included in the Credit Scrub Kit?\"",
    answer: "The MyCreditScrub Kit includes professionally prepared dispute templates, step-by-step guidance, checklists, tracking tools, and educational resources designed to help consumers organize information and prepare dispute letters for potential credit report inaccuracies. The kit focuses on structure, clarity, and proper documentation so you can take informed action at your own pace.",
    source: "",
  },
  {
    question: "\"Is disputing items legal? Will it hurt my score?\"",
    answer: "100% legal. The Fair Credit Reporting Act (FCRA) gives you the explicit right to dispute any information you believe is inaccurate, incomplete, or unverifiable. This is federal law — not a loophole. And no, disputing cannot hurt your score. The only thing that can hurt your score is the inaccurate information staying on your report.",
    source: "FCRA Section 611",
  },
  {
    question: "\"Why shouldn't I just pay a credit dispute company?\"",
    answer: "Many credit dispute services charge ongoing monthly fees that can add up over time. The DIY Credit Scrub Kit is a one-time purchase designed to help consumers understand the dispute process, organize their information, and prepare properly documented dispute letters themselves.\n\nIf you want added confidence, you can also choose Packet Review, where a professional reviews your paperwork before you send it. No outcome guarantees. Results vary. The focus is education, structure, and informed consumer action.",
    source: "",
  },
  {
    question: "\"What if my disputes don't work?\"",
    answer: "Not every credit report item will change. Accurate and properly reported information may remain until it naturally ages off a credit report. The MyCreditScrub process is designed to help consumers identify and address potential inaccuracies, incomplete entries, or unverifiable information through proper documentation.\n\nIf you have questions about the process or need clarification on next steps, support is available to help explain the workflow and review general considerations. No outcome guarantees apply, and results vary by situation.",
    source: "",
  },
  {
    question: "\"I have collections. Can those be removed?\"",
    answer: "Yes, collections are often the easiest items to dispute successfully. Collection agencies frequently lack proper documentation, report inaccurate amounts, or fail to follow the debt validation process. Our kit includes specific templates for collection disputes, plus negotiation scripts for 'pay-for-delete' arrangements when deletion through disputes isn't possible.",
    source: "FDCPA Guidelines",
  },
];

const FAQ = () => {
  return (
    <section id="faq" className="py-10 lg:py-12 bg-muted/30">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          {/* Which option helper block */}
          <div className="bg-card rounded-xl p-5 border border-border/50 mb-8">
            <h3 className="text-lg font-bold text-foreground mb-3 text-center">Which option should I choose?</h3>
            <div className="space-y-2">
              <Link to="/templates" className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <BookOpen className="w-4 h-4 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground flex-1">If you are asking "what do I dispute first" — start with the <span className="font-semibold text-foreground">DIY Kit</span>.</p>
                <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <Link to="/templates" className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <FileSearch className="w-4 h-4 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground flex-1">If you already wrote letters and want a pro to review them — choose <span className="font-semibold text-foreground">Packet Review</span>.</p>
                <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <Link to="/done-for-you" className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Briefcase className="w-4 h-4 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground flex-1">If you want the packet prepared for you — choose <span className="font-semibold text-foreground">Done-For-You</span>.</p>
                <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </div>
          </div>

          {/* Section header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
              <MessageCircle className="w-4 h-4 text-primary-dark" />
              <span className="text-sm font-semibold text-primary-dark">Real Questions from Real People</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Questions From <span className="text-gradient">Reddit & Forums</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              We researched what people actually ask online. Here are honest answers.
            </p>
          </div>

          {/* Accordion */}
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-card rounded-xl px-6 border border-border/50 shadow-custom-sm data-[state=open]:shadow-custom-md transition-shadow"
              >
                <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  <p>{faq.answer}</p>
                  {faq.source && (
                    <span className="block mt-3 text-xs text-muted-foreground italic">
                      Reference: {faq.source}
                    </span>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Compliance note */}
          <div className="mt-8 bg-card rounded-xl p-6 border border-border/50">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">Important Disclosure</h4>
                <div className="text-sm text-muted-foreground leading-relaxed space-y-2">
                  <p>
                    My Credit Scrub provides educational materials and template letters to help you understand and exercise your legal rights under the Fair Credit Reporting Act. We do not guarantee specific credit score improvements — results depend on your individual situation and whether items are legitimately disputable. For personalized financial advice, consult a qualified professional.
                  </p>
                  <p>
                    We do not promise score increases or deletions. We provide educational tools and document preparation support. Accurate negative items generally cannot be removed simply because they are negative.
                  </p>
                  <p>
                    For free credit reports authorized by law, use <a href="https://www.annualcreditreport.com" target="_blank" rel="noopener noreferrer" className="text-primary-dark hover:underline font-medium">AnnualCreditReport.com</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
